<template>
    <div class="page-container">
      <div class="picker-wrapper">
        
        <!-- Top Header Presentation -->
        <header class="page-header">
          <h1 class="main-title">Select Payment Date</h1>
          <p class="main-subtitle">Choose a fixed date to receive every year</p>
        </header>
  
        <!-- Calendar Main Card Surface -->
        <main class="calendar-card">
          <div class="cardd-header">
            <span class="header-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
            </span>
            <h2 class="cardd-title">Set Payment Date</h2>
          </div>
  
          <!-- Month Navigation Bar -->
          <div class="month-selector">
            <span class="current-month">{{ currentMonthYearDisplay }}</span>
            <div class="nav-arrows">
              <button class="arrow-btn" @click="changeMonth(-1)" aria-label="Previous month">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <button class="arrow-btn" @click="changeMonth(1)" aria-label="Next month">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
          </div>
  
          <!-- Days Grid Table Structure -->
          <div class="calendar-grid">
            <!-- Weekday Labels -->
            <span v-for="dayLabel in weekDays" :key="dayLabel" class="grid-label">{{ dayLabel }}</span>
  
            <!-- Previous Month Leading Tail Days -->
            <button 
              v-for="(day, index) in prevMonthDays" 
              :key="'prev-' + index" 
              class="grid-day trailing"
              disabled
            >
              {{ day }}
            </button>
  
            <!-- Current Active Month Grid Numbers -->
            <button 
              v-for="day in currentMonthDaysCount" 
              :key="'curr-' + day" 
              class="grid-day"
              :class="{ 'active-selected': isSelected(day) }"
              @click="selectDay(day)"
            >
              {{ day }}
            </button>
  
            <!-- Next Month Leading Extension Days -->
            <button 
              v-for="(day, index) in nextMonthDays" 
              :key="'next-' + index" 
              class="grid-day trailing"
              disabled
            >
              {{ day }}
            </button>
          </div>
        </main>
  
        <!-- Bottom System Recommended Summary Card Area -->
        <footer class="recommendation-banner">
          <div class="recommendation-content">
            <div class="badge-tag">SELECTED DATE</div>
            <span class="context-label">Your Payout Date</span>
            <h3 class="display-date">{{ formattedSelectedDate }}</h3>
            <span class="recurrence-label">(Every Year)</span>
          </div>
          
          <!-- Large Decorative Abstract Background Star Check Seal -->
          <div class="decorative-seal">
            <svg viewBox="0 0 100 100" fill="currentColor">
              <path d="M50 0 L59 13 L74 7 L77 23 L93 23 L89 39 L100 50 L89 61 L93 77 L77 77 L74 93 L59 87 L50 100 L41 87 L26 93 L23 77 L7 77 L11 61 L0 50 L11 39 L7 23 L23 23 L26 7 L41 13 Z" />
              <path d="M35 50 L45 60 L68 35" fill="none" stroke="#0047ff" stroke-width="8" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </div>
        </footer>

        <div class="button-holder">
          <button class="btn main" @click="routeTo()">Continue</button>
          <button class="btn cancel" @click="goBack()">Cancel</button>
        </div>
  
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'DateSelected',
    data() {
      const now = new Date();
      return {
        // Default to May 2026 as shown in the design system mockup
        // currentDate: new Date(2026, 6, 1), 
        currentDate: new Date(now.getFullYear(), now.getMonth(), 1),
        // Initialize chosen dynamic day value as the 5th
        selectedDay: now.getDate(),
        selectedMonth: now.getMonth(),
        selectedYear: now.getFullYear(),
        weekDays: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
      };
    },
    computed: {
      currentMonthYearDisplay() {
        const options = { month: 'long', year: 'numeric' };
        return this.currentDate.toLocaleDateString('en-US', options).toUpperCase();
      },
      currentMonthDaysCount() {
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();
       
        // const paddedDay = String(this.selectedDay).padStart(2, '0')
        // const paddedMonth = String(this.selectedMonth).padStart(2, '0')

        // const harmonizedDate = `${paddedMonth + 1}-${paddedDay}`;
        // const existingSessionData = sessionStorage.getItem('unitSelections');
        // const currentObject = existingSessionData ? JSON.parse(existingSessionData) : {};
        // currentObject['harmonizeDate'] = harmonizedDate;
        // sessionStorage.setItem('unitSelections', JSON.stringify(currentObject));
        return new Date(year, month + 1, 0).getDate();
      },
      prevMonthDays() {
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();
        const firstDayIndex = new Date(year, month, 1).getDay();
        const prevMonthDaysCount = new Date(year, month, 0).getDate();
        
        const days = [];
        for (let i = firstDayIndex - 1; i >= 0; i--) {
          days.push(prevMonthDaysCount - i);
        }
        return days;
      },
      nextMonthDays() {
        const year = this.currentDate.getFullYear();
        const month = this.currentDate.getMonth();
        const lastDayIndex = new Date(year, month + 1, 0).getDay();
        
        const nextDaysCount = 6 - lastDayIndex;
        const days = [];
        for (let i = 1; i <= nextDaysCount; i++) {
          days.push(i);
        }
        return days;
      },
      formattedSelectedDate() {
        const tempDate = new Date(this.selectedYear, this.selectedMonth, this.selectedDay);
        const day = tempDate.getDate();
        const monthStr = tempDate.toLocaleDateString('en-US', { month: 'long' });
        
        // Compute correct English ordinal suffixes (1st, 2nd, 3rd, 4th...)
        let suffix = 'th';
        if (day % 10 === 1 && day % 100 !== 11) suffix = 'st';
        else if (day % 10 === 2 && day % 100 !== 12) suffix = 'nd';
        else if (day % 10 === 3 && day % 100 !== 13) suffix = 'rd';
  
        return `${day}${suffix} ${monthStr}`;
      }
    },
    methods: {
      goBack() {
        this.$router.go(-1)
      },
      routeTo() {
        this.$router.push("harmonized-summary");
      },
      changeMonth(direction) {
        const newDate = new Date(this.currentDate);
        newDate.setMonth(newDate.getMonth() + direction);
        this.currentDate = newDate;
      },
      selectDay(day) {
        this.selectedDay = day;
        this.selectedMonth = this.currentDate.getMonth();
        this.selectedYear = this.currentDate.getFullYear();

        const paddedDay = String(this.selectedDay).padStart(2, '0')
        
        const paddedMonth = String(`${this.selectedMonth + 1}`).padStart(2, '0')

        const harmonizedDate = `${paddedMonth}-${paddedDay}`;
        const existingSessionData = sessionStorage.getItem('unitSelections');
        const currentObject = existingSessionData ? JSON.parse(existingSessionData) : {};
        currentObject['harmonizeDate'] = harmonizedDate;
        sessionStorage.setItem('unitSelections', JSON.stringify(currentObject));
      },
      isSelected(day) {
        return (
          this.selectedDay === day &&
          this.selectedMonth === this.currentDate.getMonth() &&
          this.selectedYear === this.currentDate.getFullYear()
        );
      }
    }
  };
  </script>


<style scoped lang="scss">

    // Theme Variables
$bg-canvas: #f8faff;
$surface-white: #ffffff;
$brand-blue: #0042e6;
$text-headline: #121620;
$text-secondary: #5e6675;
$text-muted: #c2c9d6;
$text-dark-blue: #061e5c;
$color-brand-dark-blue: #0033EA;

// Frame Architecture
.page-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: $bg-canvas;
  padding: 1.5rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  box-sizing: border-box;

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
}

.picker-wrapper {
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  background: #F6F8FC;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.05), 0 4px 12px rgba(0, 0, 0, 0.05), 0 12px 24px rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  padding: 15px;
}

.page-header {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding-left: 0.25rem;
  margin-top: 30px;
  margin-bottom: 10px;

  .main-title {
    font-size: 1.55rem;
    font-weight: 700;
    color: $text-headline;
    letter-spacing: -0.3px;
    font-family: Lato;
    text-align: left;
  }

  .main-subtitle {
    font-size: 0.98rem;
    color: $text-secondary;
    font-family: Lato;
    text-align: left;
  }
}

// Interactive Calendar Surface
.calendar-card {
  background-color: $surface-white;
  border-radius: 20px;
  padding: 1.8rem 1.6rem;
//   box-shadow: 0 10px 40px rgba(18, 22, 32, 0.03);
  box-shadow: 0px 4px 20px 0px #0000000D;
//   margin: 15px;
  display: flex;
  flex-direction: column;
  width: 100%;

  .cardd-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 2rem;
    font-family: Lato;
    text-align: left;

    .header-icon {
      color: $brand-blue;
      width: 22px;
      height: 22px;
      display: flex;
      align-items: center;
    }

    .cardd-title {
      font-size: 1.22rem;
      font-weight: 700;
      color: $text-dark-blue;
      font-family: Lato;
    }
  }

  .month-selector {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.8rem;
    padding: 0 0.4rem;

    .current-month {
      font-size: 0.88rem;
      font-weight: 700;
      color: $text-headline;
      letter-spacing: 0.5px;
      font-family: Lato;
    }

    .nav-arrows {
      display: flex;
      gap: 1.1rem;

      .arrow-btn {
        background: none;
        border: none;
        color: $text-secondary;
        width: 18px;
        height: 18px;
        cursor: pointer;
        display: flex;
        align-items: center;
        transition: transform 0.1s ease;

        &:active {
          transform: scale(0.9);
        }

        svg {
          width: 100%;
          height: 100%;
        }
      }
    }
  }
}

// Layout Calculation Grids
.calendar-grid {
  display: grid;
//   grid-template-columns: repeat(7, 1fr);
grid-template-columns: repeat(7, minmax(0, 1fr)); 
  row-gap: 1.25rem;
  column-gap: 0.4rem;
  justify-items: center;
  align-items: center;

  .grid-label {
    font-size: 0.88rem;
    font-weight: 500;
    color: $text-secondary;
    padding-bottom: 0.4rem;
    text-align: center;
    width: 100%;
    font-family: Lato;
  }

  .grid-day {
    background: none;
    border: none;
    width: 100%;
    max-width: 42px;
    aspect-ratio: 1 / 1; 
    font-size: 0.98rem;
    font-weight: 500;
    color: $text-headline;
    // width: 42px;
    // height: 42px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &[disabled] {
      pointer-events: none;
    }

    &.trailing {
      color: $text-muted;
    }

    // Dynamic Selected State Highlight
    &.active-selected {
      background-color: $brand-blue;
      color: $surface-white;
      font-weight: 600;
      box-shadow: 0 6px 16px rgba(0, 66, 230, 0.35);
    }
  }
}

// Dynamic Footer Layout
.recommendation-banner {
  background-color: $brand-blue;
  color: $surface-white;
  border-radius: 20px;
  padding: 1.8rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 12px 30px rgba(0, 66, 230, 0.2);

  .recommendation-content {
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 2;

    .badge-tag {
      background-color: rgba(255, 255, 255, 0.15);
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.8px;
      padding: 0.4rem 0.8rem;
      border-radius: 30px;
      width: fit-content;
      margin-bottom: 1.1rem;
      text-align: left;
      font-family: Lato;
    }

    .context-label {
      font-size: 0.92rem;
      opacity: 0.8;
      margin-bottom: 0.3rem;
      text-align: left;
      font-family: Lato;
    }

    .display-date {
      font-size: 30px;
      font-weight: 700;
      letter-spacing: -0.5px;
      margin-bottom: 0.3rem;
      text-align: left;
      font-family: Lato;
    }

    .recurrence-label {
      font-size: 0.85rem;
      opacity: 0.7;
      text-align: left;
      font-family: Lato;
    }
  }

  .decorative-seal {
    position: absolute;
    right: -15px;
    top: 50%;
    transform: translateY(-50%);
    width: 140px;
    height: 140px;
    color: rgba(255, 255, 255, 0.08);
    z-index: 1;
    pointer-events: none;

    svg {
      width: 100%;
      height: 100%;
    }
  }
}

// Media Mobile Responsiveness Breakpoints
@media (max-width: 360px) {
  .calendar-card {
    // padding: 1.4rem 1rem;
    padding: 1.25rem 0.85rem;
  }
  .calendar-grid {
    row-gap: 0.75rem;
    .grid-day {
      font-size: 0.88rem;
    }
  }
//   .calendar-grid {
//     row-gap: 0.85rem;
//     .grid-day {
//       width: 36px;
//       height: 36px;
//       font-size: 0.9rem;
//     }
//   }
.recommendation-banner {
    padding: 1.25rem;
    .recommendation-content .display-date {
      font-size: 1.65rem;
    }
    .decorative-seal {
      width: 100px;
      height: 100px;
    }
}
//   .recommendation-banner {
//     padding: 1.4rem;
//     .recommendation-content .display-date {
//       font-size: 1.8rem;
//     }
//   }
}

.button-holder {
    margin-top: 30px;
    margin-bottom: 20px;
    width: 100%;
    button {
      width: 100%;
      height: 50px;
      font-family: Lato;
      box-shadow: none;
      outline: 0;
    }
    .main {
      background-color: $color-brand-dark-blue;
      color: $surface-white;
      margin-bottom: 15px;
    }
    .cancel {
      background-color: #E6F0FA;
      color: #0033EA;
    }
}

</style>
  